// src/panels/PanelPhotos.jsx
import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SectionHeader,isDeadlinePassed,parseLocalDate } from '../components/SharedComponents';

import usePhotoCaptions from '../hooks/usePhotoCaptions';
import useToast from '../hooks/useToast';
import {
  getBackendPhotoDeleteId,
  getBackendPhotoId,
  getPhotoDisplayName,
  getPhotoImageUrl,
  isRealBackendPhotoId,
} from '../hooks/useParentPhotos';

const CAPTION_MAX_CHARS = 80;

function buildBackendDisplayItem(photo) {
  const id = getBackendPhotoId(photo);
  if (!id) return null;

  return {
    kind: 'backend',
    id,
    deleteId: getBackendPhotoDeleteId(photo),
    photo,
  };
}

function isPhotoLimitResult(result) {
  const code = String(result?.code || result?.detail?.code || '').toUpperCase();
  const reason = String(result?.reason || '').toLowerCase();
  const message = String(result?.message || '').toLowerCase();

  return (
    result?.blocked ||
    reason === 'photo_limit' ||
    code === 'PHOTO_LIMIT_EXCEEDED' ||
    message.includes('photo limit') ||
    message.includes('personal photo limit')
  );
}

export default function PanelPhotos({
  projectId,
  backendPhotos = [],
  displayPhotos,
  photos = [],
  onAdd,
  onRemove,
  onRemoveUpload,
  limit,
  onUpgrade,
  bookConfig,
  progress,
  childName = 'your child',
}) {
  const theme = useTheme();
  const isMobileCaptionDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [deletingId, setDeletingId] = useState(null);
  const [captionModalItem, setCaptionModalItem] = useState(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [isSavingCaption, setIsSavingCaption] = useState(false);
  const [isDeletingCaption, setIsDeletingCaption] = useState(false);

const isViewOnly = useMemo(
  () => isDeadlinePassed(bookConfig?.content_deadline),
  [bookConfig?.content_deadline]
);


  const safeBackendPhotos = Array.isArray(backendPhotos) ? backendPhotos : [];
  const safeDisplayPhotos = useMemo(() => {
    if (Array.isArray(displayPhotos)) return displayPhotos;

    return (Array.isArray(photos) ? photos : [])
      .map(buildBackendDisplayItem)
      .filter(Boolean);
  }, [displayPhotos, photos]);

  const {
    captionsByPhotoId,
    fetchingCaptionIds,
    saveCaption,
    deleteCaption,
  } = usePhotoCaptions(projectId, safeBackendPhotos);

  const numericLimit = Number(limit);
  const hasPhotoLimit = Number.isFinite(numericLimit);
  const photoLimit = hasPhotoLimit ? numericLimit : 0;

  const progressPhotoCount = Number(
    progress?.photos ??
    progress?.personal_photos ??
    bookConfig?.progress?.personal_photos
  );

  const backendDisplayCount = safeDisplayPhotos.filter(
    (item) => item.kind === 'backend'
  ).length;

  const committedPhotoCount = Math.max(
    safeBackendPhotos.length,
    backendDisplayCount,
    Number.isFinite(progressPhotoCount) ? progressPhotoCount : 0
  );

  const activeUploadCount = safeDisplayPhotos.filter(
    (item) => item.kind === 'upload' && item.upload?.status !== 'failed'
  ).length;

  const usedSlots = committedPhotoCount + activeUploadCount;
  const availableSlots = hasPhotoLimit ? Math.max(photoLimit - usedSlots, 0) : Infinity;

  const badge = activeUploadCount > 0
    ? `${committedPhotoCount} + ${activeUploadCount} uploading / ${hasPhotoLimit ? photoLimit : '∞'}`
    : `${committedPhotoCount} / ${hasPhotoLimit ? photoLimit : '∞'}`;

  const openPhotoLimitUpgrade = ({
    attempted = 1,
    remaining = availableSlots,
    currentLimit,
    usedSlotsOverride,
    upgrade,
    availableOffers,
    message,
  } = {}) => {
    onUpgrade?.({
      source: 'photos',
      reason: 'photo_limit',
      attempted,
      remaining: Number.isFinite(Number(remaining)) ? Number(remaining) : 0,
      currentLimit: Number.isFinite(Number(currentLimit))
        ? Number(currentLimit)
        : hasPhotoLimit
          ? photoLimit
          : null,
      usedSlots: Number.isFinite(Number(usedSlotsOverride))
        ? Number(usedSlotsOverride)
        : usedSlots,
      message,
      upgrade: upgrade || (availableOffers ? { available_offers: availableOffers } : undefined),
      available_offers: availableOffers,
    });
  };

  const handleAddTileClick = () => {
    if (isViewOnly) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    if (isViewOnly) return;

    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (!selectedFiles.length) return;

    try {
      const result = await onAdd?.(selectedFiles);

      if (isPhotoLimitResult(result)) {
        openPhotoLimitUpgrade({
          attempted: result?.total || selectedFiles.length,
          remaining: Number.isFinite(Number(result?.remainingSlots))
            ? Number(result.remainingSlots)
            : availableSlots,
          currentLimit: Number.isFinite(Number(result?.limit))
            ? Number(result.limit)
            : undefined,
          usedSlotsOverride: Number.isFinite(Number(result?.current))
            ? Number(result.current)
            : undefined,
          upgrade: result?.upgrade,
          availableOffers: result?.available_offers,
          message: result?.message,
        });
        return;
      }

      if (result?.ok === false) {
        toast.error(result.message || 'Photo upload failed. Please try again.');
        return;
      }

      if (result?.total > 0) {
        const uploaded = Number(result.uploaded) || 0;
        const duplicates = Number(result.duplicates) || 0;
        const failed = Number(result.failed) || 0;

        if (failed > 0 && uploaded > 0) {
          toast.warning(`${uploaded} photo${uploaded === 1 ? '' : 's'} uploaded. ${failed} failed.`);
        } else if (failed > 0) {
          toast.error('Photo upload failed. Please try again.');
        } else if (duplicates > 0 && uploaded > 0) {
          toast.warning(`${uploaded} photo${uploaded === 1 ? '' : 's'} uploaded. ${duplicates} duplicate photo${duplicates === 1 ? '' : 's'} skipped.`);
        } else if (duplicates > 0) {
          toast.warning(`${duplicates === 1 ? 'This photo is' : 'These photos are'} already in the yearbook.`);
        } else {
          toast.success(`${uploaded} photo${uploaded === 1 ? '' : 's'} uploaded.`);
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload failed. Please try again.');
    }
  };

  const handleRemoveBackendItem = async (item) => {
    if (isViewOnly) return;

    const deleteId = item.deleteId || item.id;
    if (!isRealBackendPhotoId(deleteId)) return;

    setDeletingId(item.id);
    try {
      const result = await onRemove?.(deleteId);
      if (result?.ok === false) {
        throw result.error || new Error('Delete failed');
      }
      toast.success('Photo deleted.');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Could not delete photo. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRemoveUploadItem = (item) => {
    if (isViewOnly || item.upload?.status === 'uploading') return;
    onRemoveUpload?.(item.id);
  };

  const openCaptionModal = (item) => {
    if (isViewOnly || item.kind !== 'backend' || !isRealBackendPhotoId(item.id)) return;

    setCaptionModalItem(item);
    setCaptionDraft(captionsByPhotoId[item.id] || '');
  };

  const closeCaptionModal = () => {
    if (isSavingCaption || isDeletingCaption) return;
    setCaptionModalItem(null);
    setCaptionDraft('');
  };

  const closeCaptionModalAfterMutation = () => {
    setCaptionModalItem(null);
    setCaptionDraft('');
  };

  const handleSaveCaption = async () => {
    if (!captionModalItem || captionModalItem.kind !== 'backend') return;

    setIsSavingCaption(true);

    try {
      const result = await saveCaption(captionModalItem.id, captionDraft);
      if (result.ok) {
        toast.success(captionDraft.trim() ? 'Caption saved.' : 'Caption deleted.');
        closeCaptionModalAfterMutation();
      } else {
        toast.error('Could not save caption. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save caption:', error);
      toast.error('Could not save caption. Please try again.');
    } finally {
      setIsSavingCaption(false);
    }
  };

  const handleDeleteCaption = async () => {
    if (!captionModalItem || captionModalItem.kind !== 'backend') return;

    setIsDeletingCaption(true);

    try {
      const result = await deleteCaption(captionModalItem.id);
      if (result.ok) {
        toast.success('Caption deleted.');
        closeCaptionModalAfterMutation();
      } else {
        toast.error('Could not delete caption. Please try again.');
      }
    } catch (error) {
      console.error('Failed to delete caption:', error);
      toast.error('Could not delete caption. Please try again.');
    } finally {
      setIsDeletingCaption(false);
    }
  };

  const captionModalPhotoUrl = captionModalItem?.photo
    ? getPhotoImageUrl(captionModalItem.photo)
    : null;
  const captionModalExistingCaption = captionModalItem
    ? captionsByPhotoId[captionModalItem.id] || ''
    : '';
  const modalBusy = isSavingCaption || isDeletingCaption;
  const captionTooLong = captionDraft.length > CAPTION_MAX_CHARS;

  return (
    <Box>
      <SectionHeader
        title="Personal Photos"
        subtitle={
          isViewOnly
            ? 'The deadline has passed. Your photos are locked for printing.'
            : <>Add family photos and short captions to preserve <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>{childName}</Box>’s growing-up memories.</>
        }
        badge={badge}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(auto-fit, minmax(280px, 320px))',
            md: 'repeat(auto-fit, minmax(320px, 360px))',
            lg: 'repeat(auto-fit, minmax(340px, 380px))',
          },
          columnGap: { xs: 2, sm: 2.25, md: 2.5 },
          rowGap: { xs: 3.25, sm: 4, md: 4.5 },
          alignItems: 'start',
          justifyItems: 'stretch',
          justifyContent: 'start',
          maxWidth: { lg: 1220 },
        }}
      >

        <input
          type="file"
          accept="image/*"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={isViewOnly}
        />

        {!isViewOnly && <AddPhotoTile onClick={handleAddTileClick} />}
        
        {safeDisplayPhotos.map((item) => {
          if (item.kind === 'upload') {
            return (
              <UploadPhotoTile
                key={item.id}
                item={item}
                isViewOnly={isViewOnly}
                onRemove={handleRemoveUploadItem}
              />
            );
          }

          return (
            <BackendPhotoTile
              key={item.id}
              item={item}
              isDeleting={deletingId === item.id}
              isViewOnly={isViewOnly}
              caption={captionsByPhotoId[item.id] || ''}
              isFetchingCaption={Boolean(fetchingCaptionIds[item.id])}
              onRemove={handleRemoveBackendItem}
              onCaption={openCaptionModal}
            />
          );
        })}
      </Box>

      <Dialog
        open={!!captionModalItem}
        onClose={closeCaptionModal}
        fullWidth
        fullScreen={isMobileCaptionDialog}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 'min(1060px, calc(100vw - 64px))' },
            height: { xs: '100%', sm: 'min(760px, calc(100vh - 64px))' },
            borderRadius: '6px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 1.75,
            px: { xs: 2, sm: 3 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 850 }}>
              {captionModalExistingCaption ? 'Edit caption' : 'Add caption'}
            </Typography>
          </Box>
          <IconButton aria-label="Close caption editor" onClick={closeCaptionModal} disabled={modalBusy}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: { xs: '0 0 46%', sm: '1 1 auto' },
              minWidth: 0,
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 1.5, sm: 2 },
            }}
          >
            {captionModalPhotoUrl ? (
              <Box
                component="img"
                src={captionModalPhotoUrl}
                alt={captionModalItem ? getPhotoDisplayName(captionModalItem.photo) : 'Selected photo'}
                sx={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 0,
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', color: 'grey.400' }}>
                <CameraAltOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography sx={{ fontSize: 13 }}>Photo preview unavailable</Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 auto', sm: '0 0 380px' },
              p: { xs: 2, sm: 3 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: 'background.default',
              minHeight: 0,
            }}
          >
            <Box sx={{ minHeight: 0 }}>
              {captionModalExistingCaption && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    onClick={handleDeleteCaption}
                    disabled={modalBusy}
                    color="error"
                    size="small"
                    startIcon={isDeletingCaption ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon sx={{ fontSize: 17 }} />}
                    sx={{ px: 0, minWidth: 'auto', fontWeight: 800, '&:hover': { bgcolor: 'transparent' } }}
                  >
                    Delete caption
                  </Button>
                </Box>
              )}
              <TextField
                autoFocus={!isMobileCaptionDialog}
                fullWidth
                multiline
                minRows={isMobileCaptionDialog ? 6 : 14}
                maxRows={isMobileCaptionDialog ? 8 : 18}
                variant="outlined"
                placeholder={`Write a short caption about this memory for ${childName}.`}
                value={captionDraft}
                onChange={(event) => {
                  const nextValue = event.target.value || '';
                  setCaptionDraft(nextValue.slice(0, CAPTION_MAX_CHARS));
                }}
                disabled={modalBusy}
                error={captionTooLong}
                inputProps={{ maxLength: CAPTION_MAX_CHARS }}
                helperText={`${captionDraft.length}/${CAPTION_MAX_CHARS}`}
                FormHelperTextProps={{
                  sx: {
                    mx: 0,
                    textAlign: 'right',
                    color: captionTooLong ? 'error.main' : 'text.secondary',
                    fontSize: 12,
                    fontWeight: 750,
                  },
                }}
                sx={softCaptionTextareaSx}
              />
            </Box>

            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={closeCaptionModal} disabled={modalBusy} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleSaveCaption}
                variant="contained"
                disableElevation
                disabled={modalBusy || captionTooLong}
                sx={{ borderRadius: '6px' }}
              >
                {isSavingCaption ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function AddPhotoTile({ onClick }) {
  return (
    <Box sx={{ ...photoCardSx, width: '100%' }}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          ...photoFrameSx,
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          border: '1.5px dashed',
          borderColor: 'grey.300',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          gap: 0.65,
          transition: 'border-color 180ms ease, background-color 180ms ease',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          font: 'inherit',
          appearance: 'none',
          WebkitAppearance: 'none',
          p: 0,
          '&:hover': {
            borderColor: 'grey.700',
            bgcolor: 'grey.50',
          },
        }}
      >
        <AddIcon sx={{ fontSize: 34, color: 'grey.500' }} />
        <Typography sx={{ fontSize: 14, color: 'grey.900', fontWeight: 850 }}>
          Add photos
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: 'grey.500' }}>
          Select multiple photos
        </Typography>
      </Box>

      <Box sx={captionFooterSx} />
    </Box>
  );
}

function UploadPhotoTile({ item, isViewOnly, onRemove }) {
  const upload = item.upload || {};
  const uploadUrl = getPhotoImageUrl(upload);
  const isUploading = upload.status === 'uploading';
  const isFailed = upload.status === 'failed';

  return (
    <Box sx={photoCardSx}>
      <Box sx={photoFrameSx}>
        {uploadUrl ? (
          <Box
            component="img"
            className="photo-image"
            src={uploadUrl}
            alt={getPhotoDisplayName(upload)}
            sx={photoImageSx}
          />
        ) : (
          <EmptyPhotoFallback label={getPhotoDisplayName(upload)} />
        )}

        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.68)',
            }}
          >
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography sx={{ fontSize: 12, color: 'text.primary', fontWeight: 850 }}>
              Uploading
            </Typography>
          </Box>
        )}

        {isFailed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              px: 1,
              textAlign: 'center',
              bgcolor: 'rgba(255,255,255,0.9)',
            }}
          >
            <Typography sx={{ fontSize: 13, color: 'error.main', fontWeight: 850 }}>
              Upload failed
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {upload.name}
            </Typography>
          </Box>
        )}

        {isFailed && !isViewOnly && (
          <IconButton
            aria-label="Dismiss failed upload"
            className="photo-action-button"
            onClick={() => onRemove(item)}
            size="small"
            sx={floatingIconButtonSx({ top: 8, right: 8 })}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>

      <Box sx={captionFooterSx}>
        <Typography sx={captionTextSx}>
          {isUploading ? 'Uploading photo...' : isFailed ? 'This photo did not upload.' : 'Preparing photo...'}
        </Typography>
      </Box>
    </Box>
  );
}

function BackendPhotoTile({ item, isDeleting, isViewOnly, caption, isFetchingCaption, onRemove, onCaption }) {
  const photo = item.photo || {};
  const url = getPhotoImageUrl(photo);
  const hasCaption = Boolean(caption?.trim());
  const showFooter = !isViewOnly || hasCaption || isFetchingCaption;

  return (
    <Box sx={{ ...photoCardSx, opacity: isDeleting ? 0.62 : 1 }}>
      <Box sx={photoFrameSx}>
        {url ? (
          <Box
            component="img"
            className="photo-image"
            src={url}
            alt={getPhotoDisplayName(photo)}
            sx={photoImageSx}
          />
        ) : (
          <EmptyPhotoFallback label={getPhotoDisplayName(photo)} />
        )}

        <Box className="photo-hover-shade" sx={photoHoverShadeSx} />

        {isDeleting && (
          <Box sx={centerOverlaySx}>
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {!isDeleting && !isViewOnly && (
          <IconButton
            aria-label="Delete photo"
            className="photo-action-button"
            onClick={() => onRemove(item)}
            size="small"
            sx={floatingIconButtonSx({ top: 8, right: 8 })}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {showFooter && (
        <Box sx={captionFooterSx}>
          {isFetchingCaption ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minHeight: 45 }}>
              <CircularProgress size={15} />
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 750 }}>
                Loading caption
              </Typography>
            </Box>
          ) : isViewOnly ? (
            <Typography sx={captionTextSx}>{caption}</Typography>
          ) : (
            <Box
              component="button"
              type="button"
              aria-label={hasCaption ? 'Edit caption' : 'Add caption'}
              onClick={() => onCaption(item)}
              sx={captionButtonSx}
            >
              <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <Typography sx={hasCaption ? captionTextSx : emptyCaptionTextSx}>
                  {hasCaption ? caption : 'No caption yet'}
                </Typography>
              </Box>
              <Box className="caption-icon-wrap" sx={captionIconWrapSx}>
                {hasCaption ? (
                  <EditOutlinedIcon sx={captionIconSx} />
                ) : (
                  <AddCommentOutlinedIcon sx={captionIconSx} />
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function EmptyPhotoFallback({ label }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        bgcolor: 'grey.100',
        color: 'grey.600',
        textAlign: 'center',
        px: 2,
      }}
    >
      <CameraAltOutlinedIcon sx={{ fontSize: 34 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 750 }} noWrap>
        {label}
      </Typography>
    </Box>
  );
}

const photoCardSx = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  bgcolor: 'transparent',
};

const photoFrameSx = {
  position: 'relative',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  bgcolor: 'grey.100',
  borderRadius: 0,
  border: '1px solid',
  borderColor: 'grey.200',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
  '&:hover .photo-image': {
    transform: 'scale(1.025)',
  },
  '&:hover .photo-hover-shade': {
    opacity: 1,
  },
  '&:hover .photo-action-button': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:focus-within .photo-action-button': {
    opacity: 1,
    transform: 'translateY(0)',
  },
};

const photoImageSx = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  borderRadius: 0,
  transform: 'scale(1)',
  transition: 'transform 320ms ease',
};

const photoHoverShadeSx = {
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 220ms ease',
  bgcolor: 'rgba(0,0,0,0.08)',
};

const captionFooterSx = {
  pt: 1,
  minHeight: 42,
};

const captionButtonSx = {
  width: '100%',
  minHeight: 36,
  px: 0,
  py: 0.6,
  border: 0,
  borderRadius: '6px',
  bgcolor: 'transparent',
  color: 'text.primary',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  textAlign: 'left',
  font: 'inherit',
  transition: 'color 160ms ease',
  '&:hover': {
    color: 'primary.main',
  },
  '&:hover .caption-icon-wrap': {
    bgcolor: 'grey.100',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 3,
  },
};

const captionTextSx = {
  fontSize: 13,
  lineHeight: 1.4,
  color: 'text.secondary',
  fontWeight: 500,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const emptyCaptionTextSx = {
  ...captionTextSx,
  color: 'grey.500',
  fontWeight: 500,
};

const captionIconWrapSx = {
  width: 32,
  height: 32,
  borderRadius: '6px',
  flex: '0 0 auto',
  bgcolor: 'grey.50',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 160ms ease',
};

const captionIconSx = {
  fontSize: 19,
  color: 'inherit',
};

const softCaptionTextareaSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    bgcolor: 'grey.50',
    alignItems: 'flex-start',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& textarea': {
    fontSize: 15.5,
    lineHeight: 1.55,
  },
};

const centerOverlaySx = {
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(255,255,255,0.48)',
};

function floatingIconButtonSx(position) {
  return {
    position: 'absolute',
    zIndex: 4,
    width: 30,
    height: 30,
    borderRadius: '6px',
    bgcolor: 'rgba(58,58,64,0.78)',
    color: '#fff',
    opacity: 0.95,
    transform: 'translateY(0)',
    transition: 'opacity 180ms ease, transform 180ms ease, background-color 180ms ease',
    ...position,
    '&.MuiIconButton-root': {
      borderRadius: '6px',
    },
    '&:hover': { bgcolor: 'rgba(58,58,64,0.94)' },
    '&:focus-visible': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  };
}